import React, { useState } from 'react'
import { Card, Table, Tag, Space, Button, Input, Select, Row, Col, Typography, Modal, Form, message, DatePicker } from 'antd'
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  ExportOutlined,
} from '@ant-design/icons'

const { Title } = Typography
const { Search } = Input
const { Option } = Select
const { TextArea } = Input

// 模拟任务数据
const tasksData = [
  {
    key: '1',
    title: 'Haul 家居店上架 10 个商品',
    type: 'listing',
    platform: 'haul',
    status: 'completed',
    priority: 'high',
    assignee: '张三',
    creator: '李四',
    due_date: '2026-09-15',
    created_at: '2026-08-01',
  },
  {
    key: '2',
    title: 'Walmart 质量分优化',
    type: 'operation',
    platform: 'walmart',
    status: 'in_progress',
    priority: 'medium',
    assignee: '李四',
    creator: '王五',
    due_date: '2026-09-20',
    created_at: '2026-08-05',
  },
  {
    key: '3',
    title: 'Joom 促销活动报名',
    type: 'operation',
    platform: 'joom',
    status: 'pending',
    priority: 'high',
    assignee: '王五',
    creator: '赵六',
    due_date: '2026-09-18',
    created_at: '2026-08-08',
  },
  {
    key: '4',
    title: 'Ozon 广告投放优化',
    type: 'operation',
    platform: 'ozon',
    status: 'blocked',
    priority: 'urgent',
    assignee: '赵六',
    creator: '钱七',
    due_date: '2026-09-12',
    created_at: '2026-08-10',
  },
  {
    key: '5',
    title: 'Shopify SEO 优化',
    type: 'seo',
    platform: 'shopify',
    status: 'in_progress',
    priority: 'low',
    assignee: '钱七',
    creator: '张三',
    due_date: '2026-09-25',
    created_at: '2026-08-12',
  },
  {
    key: '6',
    title: '数据分析报表生成',
    type: 'data',
    platform: 'other',
    status: 'pending',
    priority: 'medium',
    assignee: '张三',
    creator: '李四',
    due_date: '2026-09-22',
    created_at: '2026-08-15',
  },
  {
    key: '7',
    title: '系统功能开发',
    type: 'development',
    platform: 'other',
    status: 'in_progress',
    priority: 'high',
    assignee: '李四',
    creator: '王五',
    due_date: '2026-09-30',
    created_at: '2026-08-18',
  },
]

const platformColors: Record<string, string> = {
  haul: '#f50',
  walmart: '#1890ff',
  joom: '#52c41a',
  ozon: '#722ed1',
  shopify: '#13c2c2',
  other: '#8c8c8c',
}

const statusColors: Record<string, string> = {
  completed: 'success',
  in_progress: 'processing',
  pending: 'warning',
  blocked: 'error',
  cancelled: 'default',
}

const priorityColors: Record<string, string> = {
  low: 'green',
  medium: 'blue',
  high: 'orange',
  urgent: 'red',
}

const typeColors: Record<string, string> = {
  listing: '#f50',
  operation: '#1890ff',
  seo: '#52c41a',
  data: '#722ed1',
  development: '#13c2c2',
}

const columns = [
  {
    title: '任务名称',
    dataIndex: 'title',
    key: 'title',
    width: 300,
    sorter: (a: any, b: any) => a.title.localeCompare(b.title),
  },
  {
    title: '类型',
    dataIndex: 'type',
    key: 'type',
    width: 100,
    render: (type: string) => (
      <Tag color={typeColors[type]}>
        {type === 'listing' ? '上架' :
         type === 'operation' ? '运营' :
         type === 'seo' ? 'SEO' :
         type === 'data' ? '数据' : '开发'}
      </Tag>
    ),
    filters: [
      { text: '上架', value: 'listing' },
      { text: '运营', value: 'operation' },
      { text: 'SEO', value: 'seo' },
      { text: '数据', value: 'data' },
      { text: '开发', value: 'development' },
    ],
    onFilter: (value: any, record: any) => record.type === value,
  },
  {
    title: '平台',
    dataIndex: 'platform',
    key: 'platform',
    width: 100,
    render: (platform: string) => (
      <Tag color={platformColors[platform]}>
        {platform.toUpperCase()}
      </Tag>
    ),
    filters: [
      { text: 'HAUL', value: 'haul' },
      { text: 'WALMART', value: 'walmart' },
      { text: 'JOOM', value: 'joom' },
      { text: 'OZON', value: 'ozon' },
      { text: 'SHOPIFY', value: 'shopify' },
      { text: '其他', value: 'other' },
    ],
    onFilter: (value: any, record: any) => record.platform === value,
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    render: (status: string) => (
      <Tag color={statusColors[status]}>
        {status === 'completed' ? '已完成' :
         status === 'in_progress' ? '进行中' :
         status === 'pending' ? '待办' :
         status === 'blocked' ? '阻塞' : '已取消'}
      </Tag>
    ),
    filters: [
      { text: '已完成', value: 'completed' },
      { text: '进行中', value: 'in_progress' },
      { text: '待办', value: 'pending' },
      { text: '阻塞', value: 'blocked' },
      { text: '已取消', value: 'cancelled' },
    ],
    onFilter: (value: any, record: any) => record.status === value,
  },
  {
    title: '优先级',
    dataIndex: 'priority',
    key: 'priority',
    width: 100,
    render: (priority: string) => (
      <Tag color={priorityColors[priority]}>
        {priority === 'low' ? '低' :
         priority === 'medium' ? '中' :
         priority === 'high' ? '高' : '紧急'}
      </Tag>
    ),
    sorter: (a: any, b: any) => {
      const order = { urgent: 0, high: 1, medium: 2, low: 3 }
      return order[a.priority as keyof typeof order] - order[b.priority as keyof typeof order]
    },
  },
  {
    title: '负责人',
    dataIndex: 'assignee',
    key: 'assignee',
    width: 100,
  },
  {
    title: '截止日期',
    dataIndex: 'due_date',
    key: 'due_date',
    width: 120,
    sorter: (a: any, b: any) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime(),
  },
  {
    title: '操作',
    key: 'action',
    width: 150,
    render: (_: any, record: any) => (
      <Space size="middle">
        <Button type="link" size="small" onClick={() => message.info(`查看任务: ${record.title}`)}>查看</Button>
        <Button type="link" size="small" onClick={() => message.info(`编辑任务: ${record.title}`)}>编辑</Button>
        <Button type="link" size="small" danger onClick={() => {
          Modal.confirm({
            title: '确认删除',
            content: `确定要删除任务「${record.title}」吗？`,
            onOk: () => message.success('任务已删除'),
          })
        }}>删除</Button>
      </Space>
    ),
  },
]

const Tasks: React.FC = () => {
  const [searchText, setSearchText] = useState('')
  const [filteredData, setFilteredData] = useState(tasksData)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [form] = Form.useForm()

  // 筛选器状态
  const [filterPlatform, setFilterPlatform] = useState<string | undefined>(undefined)
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined)
  const [filterPriority, setFilterPriority] = useState<string | undefined>(undefined)

  const applyFilters = () => {
    let filtered = tasksData

    // 搜索
    if (searchText) {
      const q = searchText.toLowerCase()
      filtered = filtered.filter(
        (task) =>
          task.title.toLowerCase().includes(q) ||
          task.assignee.toLowerCase().includes(q)
      )
    }

    // 平台筛选
    if (filterPlatform) {
      filtered = filtered.filter((task) => task.platform === filterPlatform)
    }

    // 状态筛选
    if (filterStatus) {
      filtered = filtered.filter((task) => task.status === filterStatus)
    }

    // 优先级筛选
    if (filterPriority) {
      filtered = filtered.filter((task) => task.priority === filterPriority)
    }

    setFilteredData(filtered)
  }

  const handleSearch = (value: string) => {
    setSearchText(value)
    setTimeout(applyFilters, 0)
  }

  const handleFilterChange = () => {
    setTimeout(applyFilters, 0)
  }

  const handleAdd = () => {
    setIsModalVisible(true)
  }

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      message.success(`任务「${values.title}」创建成功`)
      setIsModalVisible(false)
      form.resetFields()
    })
  }

  const handleModalCancel = () => {
    setIsModalVisible(false)
    form.resetFields()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4}>任务中心</Title>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新建任务
          </Button>
          <Button icon={<ExportOutlined />} onClick={() => message.success('任务列表已导出')}>
            导出
          </Button>
        </Space>
      </div>

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Search
              placeholder="搜索任务名称或负责人"
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              onChange={(e) => { if (!e.target.value) { setSearchText(''); setTimeout(applyFilters, 0); } }}
            />
          </Col>
          <Col span={16}>
            <Space style={{ float: 'right' }}>
              <Select
                placeholder="选择平台"
                style={{ width: 120 }}
                allowClear
                value={filterPlatform}
                onChange={(v) => { setFilterPlatform(v); handleFilterChange(); }}
              >
                <Option value="haul">HAUL</Option>
                <Option value="walmart">WALMART</Option>
                <Option value="joom">JOOM</Option>
                <Option value="ozon">OZON</Option>
                <Option value="shopify">SHOPIFY</Option>
                <Option value="other">其他</Option>
              </Select>
              <Select
                placeholder="选择状态"
                style={{ width: 120 }}
                allowClear
                value={filterStatus}
                onChange={(v) => { setFilterStatus(v); handleFilterChange(); }}
              >
                <Option value="completed">已完成</Option>
                <Option value="in_progress">进行中</Option>
                <Option value="pending">待办</Option>
                <Option value="blocked">阻塞</Option>
                <Option value="cancelled">已取消</Option>
              </Select>
              <Select
                placeholder="选择优先级"
                style={{ width: 120 }}
                allowClear
                value={filterPriority}
                onChange={(v) => { setFilterPriority(v); handleFilterChange(); }}
              >
                <Option value="urgent">紧急</Option>
                <Option value="high">高</Option>
                <Option value="medium">中</Option>
                <Option value="low">低</Option>
              </Select>
              <Button icon={<FilterOutlined />} onClick={applyFilters}>筛选</Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          size="middle"
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title="新建任务"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="任务名称"
            rules={[{ required: true, message: '请输入任务名称' }]}
          >
            <Input placeholder="请输入任务名称" />
          </Form.Item>
          <Form.Item
            name="description"
            label="任务描述"
          >
            <TextArea rows={3} placeholder="请输入任务描述" />
          </Form.Item>
          <Form.Item
            name="type"
            label="任务类型"
            rules={[{ required: true, message: '请选择任务类型' }]}
          >
            <Select placeholder="请选择任务类型">
              <Option value="listing">上架</Option>
              <Option value="operation">运营</Option>
              <Option value="seo">SEO</Option>
              <Option value="data">数据</Option>
              <Option value="development">开发</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="platform"
            label="所属平台"
          >
            <Select placeholder="请选择平台" allowClear>
              <Option value="haul">HAUL</Option>
              <Option value="walmart">WALMART</Option>
              <Option value="joom">JOOM</Option>
              <Option value="ozon">OZON</Option>
              <Option value="shopify">SHOPIFY</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="priority"
            label="优先级"
            rules={[{ required: true, message: '请选择优先级' }]}
          >
            <Select placeholder="请选择优先级">
              <Option value="urgent">紧急</Option>
              <Option value="high">高</Option>
              <Option value="medium">中</Option>
              <Option value="low">低</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="assignee"
            label="负责人"
          >
            <Input placeholder="请输入负责人" />
          </Form.Item>
          <Form.Item
            name="due_date"
            label="截止日期"
          >
            <DatePicker style={{ width: '100%' }} placeholder="请选择截止日期" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Tasks
